import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clientQuoteService from '../services/clientQuoteService';
import clientService from '../services/clientService';
import productService from '../services/productService';
import ClientQuoteItemRow from '../components/ClientQuoteItemRow';
import { normalizeNumericInput, toNumber } from '../utils/numericInput';
import ClientSearch from '../components/ClientSearch';
import BuyerContactSelect from '../components/BuyerContactSelect';
import CreateClientModal from '../components/CreateClientModal';
import ConfirmActionModal from '../components/ConfirmActionModal';
import ColumnVisibilityDropdown from '../components/ColumnVisibilityDropdown';
import { PUBLICO_GENERAL_RFC } from '../constants/client';
import { useNotification } from '../context/NotificationContext';
import '../styles/clientQuotes.css';

const ClientQuoteFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!id;
  const { notify } = useNotification();

  const [clientQuote, setClientQuote] = useState({
    client_id: null,
    quote_number: null,
    status: null,
    id: null,
    client_name: '',
    client_contacts: [],
    client_tax_id: null,
    buyer_contact: { id: null, name: '', phone: '', email: '' },
    includeTaxes: true,
    taxPercentage: 16,
    items: [],
    subtotal: 0,
    taxes: 0,
    grandTotal: 0,
    conditions_and_terms: 'Los precios son expresados en Pesos Mexicanos.\nLa cotización tiene vigencia de 2 semanas.\nNo incluye instalación (Al menos que venga en alguna partida)\nNo incluye envió (Al menos que se mencione en alguna partida)',
    delivery_time: 'deliveryTime1Week'
  });

  const isReadOnly = isEditMode && clientQuote.status === 'venta';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());

  const toggleableColumns = [
    { key: 'description', label: t('table.description') },
    { key: 'finishing', label: t('clientQuotes.finishing') },
    { key: 'quantity', label: t('table.quantity') },
    { key: 'increasePercentage', label: t('clientQuotes.increasePercentage') },
    { key: 'finalUnitPrice', label: t('clientQuotes.finalUnitPrice') }
  ];

  const handleToggleColumn = (key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const productsRes = await productService.listProducts({});
        setProducts(productsRes.data.results || []);
      } catch (err) {
        console.error('Error loading products:', err);
      }

      if (isEditMode) {
        await loadClientQuote();
      } else if (location.state?.quoteGeneratorData) {
        const quoteGenData = location.state.quoteGeneratorData;
        const mappedItems = renumberItems(quoteGenData.items.map((item) => withComputedPricing({
          productId: item.productId,
          productName: item.productName,
          product: item,
          measure: { id: item.measureId, name: item.measureName, abbreviation: item.measureAbbr },
          description: item.productName,
          area: (item.measureName === 'Metro Cuadrado' || item.measureName === 'Centimetro Cuadrado') ? `${item.width} x ${item.height}` : 'N/A',
          finishing: item.productDescription || '',
          quantity: item.quantity,
          unit_price: item.price,
          increase_percentage: 0
        })));

        const baseQuote = {
          client_id: null,
          client_name: quoteGenData.client || '',
          client_contacts: [],
          buyer_contact: { id: 0, name: quoteGenData.buyer || '', phone: '', email: '' },
          includeTaxes: quoteGenData.includeTaxes ?? true,
          taxPercentage: quoteGenData.taxPercentage,
          items: mappedItems,
          subtotal: 0,
          taxes: 0,
          grandTotal: 0,
          conditions_and_terms: 'Los precios son expresados en Pesos Mexicanos.\nLa cotización tiene vigencia de 2 semanas.\nNo incluye instalación (Al menos que venga en alguna partida)\nNo incluye envió (Al menos que se mencione en alguna partida)',
          delivery_time: 'deliveryTime1Week'
        };

        const calculated = calculateTotals(
          mappedItems, quoteGenData.includeTaxes ?? true, quoteGenData.taxPercentage
        );
        setClientQuote({
          ...baseQuote,
          ...calculated
        });
      }
    };

    loadInitialData();
    // Mount-only on purpose: this seeds the form from either the loaded quote
    // or the quote-generator hand-off. Re-running it would re-seed over the
    // user's edits and, for generator hand-offs, recreate the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClientQuote = async () => {
    try {
      const res = await clientQuoteService.getClientQuoteById(id);
      const data = res.data;

      let clientContacts = [];
      let clientTaxId = null;
      if (data.client) {
        try {
          const fullClientRes = await clientService.getClientById(data.client);
          clientContacts = fullClientRes.data.contacts || [];
          clientTaxId = fullClientRes.data.tax_id || null;
        } catch (err) {
          console.error('Error fetching client details:', err);
        }
      }

      // Recompute final_price/total for every loaded item rather than
      // trusting whatever was last persisted — self-heals older quotes
      // saved before this field existed (or saved with a stale total).
      const items = renumberItems((data.quote_data.items || []).map(withComputedPricing));
      // A saved quote deliberately does not inherit the "taxes on by default"
      // rule that new quotes get: it was priced and sent with whatever the
      // user chose, and flipping the flag on reopen would change a total the
      // client has already seen. The ?? false only keeps the checkbox
      // controlled for quotes saved before the flag existed.
      const totals = calculateTotals(
        items, data.quote_data.includeTaxes ?? false, data.quote_data.taxPercentage
      );

      setClientQuote({
        client_id: data.client,
        quote_number: data.quote_number,
        status: data.status,
        id: data.id,
        client_name: data.client_name || '',
        client_contacts: clientContacts,
        client_tax_id: clientTaxId,
        buyer_contact: data.buyer_contact || { id: null, name: '', phone: '', email: '' },
        includeTaxes: data.quote_data.includeTaxes ?? false,
        taxPercentage: data.quote_data.taxPercentage,
        items,
        ...totals,
        conditions_and_terms: data.quote_data.conditions_and_terms || 'Los precios son expresados en Pesos Mexicanos.\nLa cotización tiene vigencia de 2 semanas.\nNo incluye instalación (Al menos que venga en alguna partida)\nNo incluye envió (Al menos que se mencione en alguna partida)',
        delivery_time: data.quote_data.delivery_time || 'deliveryTime1Week'
      });
    } catch (err) {
      console.error('Error loading Client Quote:', err);
      notify.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const renumberItems = (items) => items.map((item, idx) => ({ ...item, number: idx + 1 }));

  /**
   * unit_price with increase_percentage applied. increase_percentage is
   * stored as a fraction (0.1 = 10%), not a raw percentage.
   */
  const calculateItemFinalPrice = (item) => {
    const unitPrice = toNumber(item.unit_price);
    const increase = toNumber(item.increase_percentage);
    return unitPrice * (1 + increase);
  };

  const calculateItemTotal = (item) => {
    const quantity = toNumber(item.quantity);
    return calculateItemFinalPrice(item) * quantity;
  };

  /**
   * Attaches final_price/total to a single item — called everywhere an
   * item is created or its price-affecting fields change, so these are
   * never left stale (e.g. 0 on a freshly-added row) for the PDF to read.
   */
  const withComputedPricing = (item) => ({
    ...item,
    final_price: parseFloat(calculateItemFinalPrice(item).toFixed(2)),
    total: parseFloat(calculateItemTotal(item).toFixed(2))
  });

  const calculateTotals = (items, includeTaxes, taxPercentage) => {
    const subtotal = items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const taxes = includeTaxes ? (subtotal * toNumber(taxPercentage) / 100) : 0;
    const grandTotal = subtotal + taxes;
    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      taxes: parseFloat(taxes.toFixed(2)),
      grandTotal: parseFloat(grandTotal.toFixed(2))
    };
  };

  const handleIncludeTaxesChange = (value) => {
    const updated = { ...clientQuote, includeTaxes: value };
    const totals = calculateTotals(updated.items, value, updated.taxPercentage);
    setClientQuote({ ...updated, ...totals });
  };

  // Stores the raw (normalised) string so the field can be cleared and never
  // accumulates a stale leading zero. Falling back to 16 here is what made an
  // emptied field snap straight back to the default.
  const handleTaxPercentageChange = (value) => {
    const percentage = normalizeNumericInput(value);
    const updated = { ...clientQuote, taxPercentage: percentage };
    const totals = calculateTotals(updated.items, updated.includeTaxes, percentage);
    setClientQuote({ ...updated, ...totals });
  };

  const handleItemChange = (itemIndex, field, value) => {
    // Functional update: ProductAutocomplete's onSelect fires this multiple
    // times synchronously (one call per field), so each call must build on
    // the previous one's result rather than a stale `clientQuote` closure,
    // or all but the last field change would be lost.
    setClientQuote((prevQuote) => {
      const updatedItems = renumberItems(prevQuote.items.map((item, idx) => {
        if (idx === itemIndex) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unit_price' || field === 'increase_percentage') {
            return withComputedPricing(updated);
          }
          return updated;
        }
        return item;
      }));
      const totals = calculateTotals(updatedItems, prevQuote.includeTaxes, prevQuote.taxPercentage);
      return { ...prevQuote, items: updatedItems, ...totals };
    });
  };

  const handleItemRemove = (itemIndex) => {
    const updatedItems = renumberItems(clientQuote.items.filter((_, idx) => idx !== itemIndex));
    const totals = calculateTotals(updatedItems, clientQuote.includeTaxes, clientQuote.taxPercentage);
    setClientQuote({ ...clientQuote, items: updatedItems, ...totals });
  };

  const handleAddRow = () => {
    const newItem = withComputedPricing({
      productId: null,
      productName: '',
      product: null,
      measure: { id: 1, name: 'Unitario', abbreviation: 'U' },
      description: '',
      area: 'N/A',
      finishing: '',
      quantity: 1,
      unit_price: 0,
      increase_percentage: 0
    });

    const updatedItems = renumberItems([...clientQuote.items, newItem]);
    const totals = calculateTotals(updatedItems, clientQuote.includeTaxes, clientQuote.taxPercentage);
    setClientQuote({ ...clientQuote, items: updatedItems, ...totals });
  };

  const validateForm = () => {
    const errors = [];
    if (!clientQuote.client_id) errors.push(t('clientQuotes.clientRequired'));
    if (!clientQuote.buyer_contact?.name?.trim()) errors.push(t('clientQuotes.buyerRequired'));
    if (clientQuote.items.length === 0) errors.push(t('clientQuotes.itemsRequired'));
    return errors;
  };

  /**
   * The single source of truth for what gets saved: quote_data duplicates
   * subtotal/taxes/grand_total (in addition to the top-level model
   * columns) so the PDF generator can read every consolidated total from
   * one place without recalculating anything.
   */
  const buildSavePayload = () => ({
    client_id: clientQuote.client_id,
    buyer_contact: clientQuote.buyer_contact,
    quote_data: {
      includeTaxes: clientQuote.includeTaxes,
      // The inputs hold raw strings so they stay editable; what is persisted
      // (and read back by the PDF generator) must be numbers.
      taxPercentage: toNumber(clientQuote.taxPercentage),
      items: clientQuote.items.map((item) => ({
        ...item,
        quantity: toNumber(item.quantity),
        unit_price: toNumber(item.unit_price)
      })),
      conditions_and_terms: clientQuote.conditions_and_terms,
      delivery_time: clientQuote.delivery_time,
      subtotal: clientQuote.subtotal,
      taxes: clientQuote.taxes,
      grand_total: clientQuote.grandTotal
    },
    subtotal: clientQuote.subtotal.toString(),
    taxes: clientQuote.taxes.toString(),
    grandTotal: clientQuote.grandTotal.toString()
  });

  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      notify.error(errors.join(', '));
      return;
    }

    setSaving(true);
    try {
      const payload = buildSavePayload();

      // isPersisted must include clientQuote.id (reactive), not just
      // isEditMode (fixed at mount from the URL) — for a brand-new quote
      // the URL never changes after the first save, so isEditMode alone
      // would stay false forever and every subsequent click here would
      // create a duplicate quote instead of updating the one just
      // created. clientQuote.id falls back to the URL's id so edit mode
      // still targets the right row even before/without the loaded
      // quote echoing its own id back.
      const isPersisted = isEditMode || !!clientQuote.id;
      let response;
      if (isPersisted) {
        response = await clientQuoteService.updateClientQuote(clientQuote.id || id, payload);
      } else {
        response = await clientQuoteService.createClientQuote(payload);
      }

      setClientQuote((prevQuote) => ({ ...prevQuote, quote_number: response.data.quote_number, id: response.data.id, status: response.data.status }));
      notify.success(t('clientQuotes.quoteSaved'));
    } catch (err) {
      console.error('Error saving Client Quote:', err);
      notify.error(t('common.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleConvertConfirm = async () => {
    setConverting(true);
    try {
      await clientQuoteService.convertToSale(clientQuote.id);
      setClientQuote((prev) => ({ ...prev, status: 'venta' }));
      notify.success(t('clientQuotes.quoteConverted'));
      setShowConvertModal(false);
    } catch (err) {
      console.error('Error converting quote to sale:', err);
      notify.error(t('clientQuotes.failedToConvertQuote'));
    } finally {
      setConverting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const handleExportPDF = async () => {
    const savedId = clientQuote.id ?? id;
    if (!savedId) {
      notify.error(t('clientQuotes.saveBeforeExport'));
      return;
    }

    try {
      setSaving(true);
      if (!isReadOnly) {
        // Save the client quote with proper payload format before exporting
        const payload = buildSavePayload();
        await clientQuoteService.updateClientQuote(savedId, payload);
      }
      // Then export to PDF
      await clientQuoteService.exportPDF(savedId);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      notify.error(t('clientQuotes.failedToExportPDF'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#3D3D3D' }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 40px', marginLeft: 'auto', marginRight: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '30px' }}>
        <h1 style={{ color: '#3D3D3D', margin: 0 }}>
          {isEditMode ? t('clientQuotes.editQuote') : t('clientQuotes.newQuote')}
        </h1>
        {clientQuote.quote_number && (
          <h1 style={{ color: '#3D3D3D', margin: 0 }}>
            {t('clientQuotes.listHeaders.quoteNumber')} {clientQuote.quote_number}
          </h1>
        )}
      </div>

      {/* Quote Details Section */}
      <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
        <h3 style={{ color: '#3D3D3D', marginTop: 0 }}>{t('clientQuotes.quoteDetails')}</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
              {t('table.client')} *
            </label>
            {isReadOnly ? (
              <p>{clientQuote.client_name}</p>
            ) : (
              <div className="flex-between gap-10">
                <div className="flex-grow">
                  <ClientSearch
                    onSelect={async (client) => {
                      let contacts = client.contacts || [];

                      // If contacts are not in the response, fetch full client details
                      if (!contacts || contacts.length === 0) {
                        try {
                          const fullClientRes = await clientService.getClientById(client.id);
                          contacts = fullClientRes.data.contacts || [];
                        } catch (err) {
                          console.error('Error fetching client details:', err);
                        }
                      }

                      setClientQuote({
                        ...clientQuote,
                        client_id: client.id,
                        client_name: client.name,
                        client_contacts: contacts,
                        client_tax_id: client.tax_id || null,
                        buyer_contact: { id: null, name: '', phone: '', email: '' }
                      });
                    }}
                    selectedClient={clientQuote.client_id ? { id: clientQuote.client_id, name: clientQuote.client_name } : null}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreateClientModal(true)}
                  className="btn btn-secondary btn-small"
                >
                  + {t('clients.createButton')}
                </button>
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
              {t('clientQuotes.buyerRequired')} *
            </label>
            {isReadOnly ? (
              <p>{clientQuote.buyer_contact?.name}</p>
            ) : clientQuote.client_id ? (
              <div style={{ position: 'relative' }}>
                <BuyerContactSelect
                  contacts={clientQuote.client_contacts || []}
                  selectedContact={clientQuote.buyer_contact}
                  allowFreeText={clientQuote.client_tax_id === PUBLICO_GENERAL_RFC}
                  onSelect={(contact) => {
                    setClientQuote({
                      ...clientQuote,
                      buyer_contact: contact
                    });
                  }}
                  onAddContact={async (newContact) => {
                    try {
                      const updatedContacts = [...(clientQuote.client_contacts || []), newContact];
                      const res = await clientService.updateClient(clientQuote.client_id, {
                        contacts: updatedContacts
                      });
                      const savedContacts = res.data.contacts || updatedContacts;
                      const addedContact = savedContacts[savedContacts.length - 1];
                      setClientQuote((prevQuote) => ({
                        ...prevQuote,
                        client_contacts: savedContacts,
                        buyer_contact: {
                          id: savedContacts.length - 1,
                          name: addedContact.name || '',
                          phone: addedContact.phone || '',
                          email: addedContact.email || ''
                        }
                      }));
                      notify.success(t('clients.clientUpdated'));
                    } catch (err) {
                      console.error('Error adding contact:', err);
                      notify.error(t('common.failedToUpdate'));
                    }
                  }}
                />
              </div>
            ) : (
              <div style={{
                padding: '10px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                color: '#666',
                fontSize: '14px'
              }}>
                {t('clientQuotes.clientRequired')}
              </div>
            )}
          </div>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={clientQuote.includeTaxes}
              onChange={(e) => handleIncludeTaxesChange(e.target.checked)}
              style={{ marginRight: '10px', cursor: 'pointer' }}
              disabled={isReadOnly}
            />
            <span style={{ color: '#3D3D3D', fontWeight: 'bold' }}>{t('clientQuotes.includeTaxes')}</span>
          </label>

          {clientQuote.includeTaxes && (
            <div style={{ marginTop: '10px', paddingLeft: '20px' }}>
              <label
                htmlFor="clientQuoteTaxPercentage"
                style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}
              >
                {t('clientQuotes.taxPercentage')}
              </label>
              <input
                id="clientQuoteTaxPercentage"
                type="text"
                inputMode="decimal"
                value={clientQuote.taxPercentage ?? ''}
                onChange={(e) => handleTaxPercentageChange(e.target.value)}
                style={{
                  width: '150px',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
                disabled={isReadOnly}
              />
            </div>
          )}
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-30">
        <div className="flex-between mb-15">
          <h3>{t('clientQuotes.items')}</h3>
          <div className="flex-center gap-10">
            <ColumnVisibilityDropdown
              columns={toggleableColumns}
              hidden={hiddenColumns}
              onToggle={handleToggleColumn}
            />
            {!isReadOnly && (
              <button
                onClick={handleAddRow}
                className="btn btn-primary"
              >
                + {t('clientQuotes.addProduct')}
              </button>
            )}
          </div>
        </div>
        <div className="items-table-wrapper">
          <table className="items-table">
            <thead>
              <tr>
                <th className="col-number col-sticky-left text-center">#</th>
                <th className="col-product col-sticky-left">{t('clientQuotes.product')}</th>
                <th className="col-calculation col-sticky-left text-center">{t('clientQuotes.calculation')}</th>
                {!hiddenColumns.has('description') && <th>{t('table.description')}</th>}
                <th className="text-center">{t('clientQuotes.measure')}</th>
                {!hiddenColumns.has('finishing') && <th>{t('clientQuotes.finishing')}</th>}
                {!hiddenColumns.has('quantity') && <th className="text-center">{t('table.quantity')}</th>}
                <th className="col-unit-price text-center">{t('clientQuotes.unitPrice')}</th>
                {!hiddenColumns.has('increasePercentage') && <th className="col-increase text-center">{t('clientQuotes.increasePercentage')}</th>}
                {!hiddenColumns.has('finalUnitPrice') && <th className="col-final-price text-right">{t('clientQuotes.finalUnitPrice')}</th>}
                <th className="col-total col-sticky-right text-right">{t('clientQuotes.total')}</th>
                <th className="col-actions col-sticky-right text-center">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {clientQuote.items.map((item, index) => (
                <ClientQuoteItemRow
                  key={index}
                  item={item}
                  index={index}
                  products={products}
                  onItemChange={handleItemChange}
                  onItemRemove={handleItemRemove}
                  hiddenColumns={hiddenColumns}
                  readOnly={isReadOnly}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals Section */}
      <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '30px', maxWidth: '400px', marginLeft: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ color: '#3D3D3D', fontWeight: 'bold' }}>{t('clientQuotes.subtotal')}:</span>
          <span style={{ color: '#3D3D3D' }}>{formatCurrency(clientQuote.subtotal)}</span>
        </div>

        {clientQuote.includeTaxes && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ color: '#3D3D3D', fontWeight: 'bold' }}>{t('clientQuotes.taxes')} ({clientQuote.taxPercentage}%):</span>
            <span style={{ color: '#3D3D3D' }}>{formatCurrency(clientQuote.taxes)}</span>
          </div>
        )}

        <div style={{ borderTop: '2px solid #ddd', paddingTop: '10px', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#3D3D3D', fontWeight: 'bold', fontSize: '18px' }}>{t('clientQuotes.grandTotal')}:</span>
          <span style={{ color: '#A0BD46', fontWeight: 'bold', fontSize: '18px' }}>{formatCurrency(clientQuote.grandTotal)}</span>
        </div>
      </div>

      {/* Conditions and Delivery Section */}
      <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
        <h3 style={{ color: '#3D3D3D', marginTop: 0 }}>{t('clientQuotes.additionalInfo')}</h3>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
            {t('clientQuotes.conditionsAndTerms')}
          </label>
          <textarea
            value={clientQuote.conditions_and_terms || ''}
            onChange={(e) => setClientQuote({ ...clientQuote, conditions_and_terms: e.target.value })}
            rows="4"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box',
              fontFamily: 'Arial, sans-serif',
              fontSize: '13px',
              resize: 'vertical'
            }}
            disabled={isReadOnly}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
              {t('clientQuotes.deliveryTime')}
            </label>
            <select
              value={clientQuote.delivery_time || 'deliveryTime1Week'}
              onChange={(e) => setClientQuote({ ...clientQuote, delivery_time: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
              disabled={isReadOnly}
            >
              <option value="deliveryTime3Days">{t('clientQuotes.deliveryTime3Days')}</option>
              <option value="deliveryTime1Week">{t('clientQuotes.deliveryTime1Week')}</option>
              <option value="deliveryTime2Weeks">{t('clientQuotes.deliveryTime2Weeks')}</option>
              <option value="deliveryTime3Weeks">{t('clientQuotes.deliveryTime3Weeks')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex-center gap-15 mb-30" style={{ maxWidth: '400px', marginLeft: 'auto' }}>
        {!isReadOnly && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
            style={{ flex: 1 }}
          >
            {saving ? t('common.loading') : t('common.saveQuote')}
          </button>
        )}

        {clientQuote.quote_number && (
          <button
            onClick={handleExportPDF}
            className="btn btn-export"
            style={{ flex: 1 }}
          >
            {t('clientQuotes.exportPDF')}
          </button>
        )}

        {isEditMode && clientQuote.status === 'cotizacion' && (
          <button
            onClick={() => setShowConvertModal(true)}
            className="btn btn-primary"
            style={{ flex: 1 }}
          >
            {t('clientQuotes.convertToSale')}
          </button>
        )}

        {isEditMode && clientQuote.status && clientQuote.status !== 'cotizacion' && (
          <span className={`table-status ${clientQuote.status}`}>
            {t(`clientQuotes.status.${clientQuote.status}`)}
          </span>
        )}

        <button
          onClick={() => navigate('/client-quotes')}
          className="btn btn-secondary"
          style={{ flex: 1 }}
        >
          {t('clientQuotes.backToList')}
        </button>
      </div>

      <CreateClientModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        onClientCreated={(client) => {
          setShowCreateClientModal(false);
          setClientQuote({
            ...clientQuote,
            client_id: client.id,
            client_name: client.name,
            client_contacts: client.contacts || [],
            client_tax_id: client.tax_id || null,
            buyer_contact: { id: null, name: '', phone: '', email: '' }
          });
        }}
      />

      <ConfirmActionModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        onConfirm={handleConvertConfirm}
        title={t('clientQuotes.convertToSale')}
        message={t('clientQuotes.confirmConvertToSale')}
        confirmLabel={t('common.confirm')}
        confirming={converting}
      />
    </div>
  );
};

export default ClientQuoteFormPage;
